<script setup lang="ts">
import { computed } from 'vue'
import { useToaster, type ToastKind } from '@/composables/useToaster'
import { useAudioPlayerStore } from '@/stores/audioPlayer'

const { toasts, dismiss, runAction } = useToaster()
const player = useAudioPlayerStore()

// Cuando la mini-barra del reproductor está visible, los toasts deben
// quedar por encima de ella (no superpuestos): 16px es el `bottom-4`
// original, y le sumamos la altura REAL de la barra (medida por ella misma
// con ResizeObserver), no un número inventado. Sin reproductor activo esto
// es exactamente 16px, igual que antes.
const bottomOffset = computed(() => (player.isMiniBarVisible ? player.miniBarHeight + 16 : 16))

const iconByKind: Record<ToastKind, string> = {
  success: 'M5 13l4 4L19 7',
  error: 'M6 18L18 6M6 6l12 12',
  warning: 'M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
}

const classByKind = computed<Record<ToastKind, string>>(() => ({
  success: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  error: 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-300',
  warning: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300',
  info: 'border-accent/40 bg-accent/10 text-accent',
}))
</script>

<template>
  <Teleport to="body">
    <div
      class="pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2 px-4 transition-[bottom] duration-200 ease-out"
      :style="{ bottom: `${bottomOffset}px` }"
    >
      <TransitionGroup name="toast">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="pointer-events-auto flex max-w-md items-start gap-2 rounded-lg border px-4 py-2.5 text-sm shadow-lg backdrop-blur"
          :class="classByKind[t.kind]"
          role="status"
        >
          <svg class="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" :d="iconByKind[t.kind]" />
          </svg>
          <span class="flex-1">{{ t.message }}</span>
          <button
            v-if="t.action"
            type="button"
            class="btn-press shrink-0 rounded px-2 py-0.5 text-xs font-semibold underline-offset-2 hover:underline"
            @click="runAction(t.id)"
          >
            {{ t.action.label }}
          </button>
          <button
            type="button"
            class="shrink-0 opacity-60 hover:opacity-100"
            aria-label="Cerrar"
            @click="dismiss(t.id)"
          >
            <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
